import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withStyles, makeStyles, createStyles } from "@material-ui/core/styles";
import { Link } from "react-router-dom";
import Grid from "@material-ui/core/Grid";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import { Tooltip, Checkbox, IconButton, Chip, Drawer, Box,  Typography, Button } from "@material-ui/core";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import Paper from "@material-ui/core/Paper";
import Pagination from "@material-ui/lab/Pagination";
import HelpIcon from "@material-ui/icons/Help";
import MapIcon from '@material-ui/icons/Map';
import { setCurrentPageNumber, setSelectedTag } from "../../actions";
import _ from "lodash";
import TagConfig from "../../config/TagConfig";
import { isSaitamaSeasonSelected } from "../../config/SaitamaCityConfig";

const useStyles = makeStyles((theme) =>
  createStyles({
    tableWrapper: {
      width: "100%",
    },
    tableWrapperSlim: {
      width: "89%",
    },
    table: {
      width: "100%",
    },
    tableHead: {
      height: 40,
      backgroundColor: "#fafafa",
    },
    tableRow: {
      height: 126,
      "&:hover": {
        backgroundColor: theme.palette.action.hover, // Use a light grey color on hover
      },
    },
    tableRowNone: {
      height: 126,
    },
    noDataCell: {
      textAlign: "center",
      padding: theme.spacing(3),
      fontSize: 14,
      color: theme.palette.text.secondary,
    },
    thumb: {
      width: 80,
    },
    thumbWrapper: {
      position: "relative",
      width: 90,
      height: 90,
      margin: "auto",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    thumbSkeleton: {
      position: "absolute",
      inset: 0,
      borderRadius: 6,
      background:
        "linear-gradient(90deg, #f2f2f2 25%, #e6e6e6 37%, #f2f2f2 63%)",
      backgroundSize: "400% 100%",
      animation: "$skeletonShimmer 1.2s ease-in-out infinite",
    },
    "@keyframes skeletonShimmer": {
      "0%": { backgroundPosition: "100% 0" },
      "100%": { backgroundPosition: "0 0" },
    },
    pagination: {
      display: "flex",
      justifyContent: "center",
      marginTop: theme.spacing(4),
      marginBottom: theme.spacing(4),
    },
    imagePopup: {
      position: "fixed", // Change to fixed so it stays within the viewport
      border: "1px solid #ccc",
      backgroundColor: "#fff",
      padding: theme.spacing(1),
      boxShadow: theme.shadows[3],
      zIndex: 1000,
    },
    tagUnSelect: {
      marginTop: theme.spacing(-2.6),
      fontSize: 9,
      "& .MuiInputLabel-root": {
        fontSize: "14px", // Smaller font size for the label (placeholder)
      },
      "& .MuiInputBase-input": {
        fontSize: "14px", // Customize input text size (if needed)
      },
    },
    tagSelect: {
      minWidth: 300,
      maxWidth: 300,
      marginTop: theme.spacing(-2.6),
      "& .MuiInputLabel-root": {
        fontSize: "14px", // Smaller font size for the label (placeholder)
      },
      "& .MuiInputBase-input": {
        fontSize: "14px", // Customize input text size (if needed)
      },
    },
    iconTooltip: {
      fontSize: 18,
      marginTop: -4,
      marginLeft: 4,
    },
    tagWrapper: {
      display: "flex",
    },
    saveButton: {
      fontWeight: "medium",
      height: 30,
      marginTop: -4,
      marginLeft: 16,
      paddingTop: 8,
    },
    tooltip: {
      fontSize: "12px", // フォントサイズを小さく
      paddingTop: "8px", // Tooltip の余白調整
    },
    icon: {
      paddingTop: "4px", // アイコンの位置調整
    },
    noImageBox: {
      all: "unset",
      width: "90px",  // 画像と同じサイズに調整
      height: "90px",
      backgroundColor: "#d9d3d3",  // 画像のグレーに近い色
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      color: "#000",  // テキストの色を薄めに
      fontSize: "14px",
      borderRadius: "6px", // 角丸の調整（適宜変更）
      margin: "auto",
      textDecoration: "none"
    }
  })
);

const ReportTable = ({
  periodType,
  setCurrentPageNumber,
  filteredTotal,
  setSelectedTag,
  handleImageSelect,
  open,
  downloadImage,
  setDownloadImage,
  filterType,
  reportData,
  reportDataNtt,
  reportDataHokuden,
  reportDataMission,
  activeDownload,
  handleDrawerOpen,
  handleDrawerClose,
  companyName,
  selectedSeason,
  pageTopLoader,
}) => {
  const classes = useStyles();
  const [tableData, setTableData] = useState([]);
  const [tagData, setTagData] = useState([]);
  const [totalAssets, setTotalAssets] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hoveredImage, setHoveredImage] = useState(null);
  const [hoverPosition, setHoverPosition] = useState({ x: 0, y: 0 });
  const [mapDrawerOpen, setMapDrawerOpen] = useState(false);
  const [selectedLatLng, setSelectedLatLng] = useState({ lat: null, lng: null });
  const [loadedImages, setLoadedImages] = useState({});
  const [loadCycle, setLoadCycle] = useState(0);
  const itemsPerPage = 10;
  const popupWidth = 300;
  const popupHeight = 300;
  const isSaitamaReport =
    companyName === "saitama-city" ||
    isSaitamaSeasonSelected(localStorage.getItem("access_key"), selectedSeason);

  useEffect(() => {
    if (typeof filteredTotal === "number") {
      setTotalAssets(filteredTotal);
    }
  }, [filteredTotal]);

  useEffect(() => {
    if (periodType === "season") {
      setTagData(TagConfig.seasonTag);
    } else {
      setTagData(TagConfig.missionTagB.concat(TagConfig.missionTagC));
    }
  }, [periodType]);

  useEffect(() => {
    const dataForTable = reportData;
    if (dataForTable.length > 0) {
      let imageDataObj = {},
        imageArray = [];
      _.each(dataForTable, function (data) {
        imageDataObj.reportId = data.reportId;
        imageDataObj.municipalityName = data.municipalityName;
        imageDataObj.prefectureName = data.prefectureName;
        imageDataObj.assetTypeName = data.assetTypeName;
        imageDataObj.signNumber = data.signNumber;
        imageDataObj.signName = data.signName;
        imageDataObj.name = data.name;
        imageDataObj.playerId = data.playerId;
        imageDataObj.placeCode = data.placeCode;
        imageDataObj.assetNum = data.assetNum;
        imageDataObj.fileName = data.fileName;
        imageDataObj.tag = data.tag;
        imageDataObj.tagTip = data.tagTip;
        imageDataObj.resultTag = data.resultTag;
        imageDataObj.user = data.user;
        imageDataObj.fileUrl = data.fileUrl;
        imageDataObj.saitamaManagementId = data.saitamaManagementId;
        const dateObj = new Date(data.occurred_datetime);
        imageDataObj.dateTime = dateObj
          .toISOString()
          .slice(0, 19)
          .replace("T", " ");
        imageDataObj.latitude = data.latitude;
        imageDataObj.longitude = data.longitude;
        imageArray.push(imageDataObj);
        imageDataObj = {};
      });
      setLoadedImages({});
      setTableData(imageArray);
    } else {
      setTableData([]);
      setTotalAssets(0);
      setLoadedImages({});
    }
  }, [reportData, filterType]);

  useEffect(() => {
    if (isSaitamaReport) return;
    const dataForTable = reportDataNtt;
    if (dataForTable.length > 0) {
      let imageDataObj = {},
        imageArray = [];
      _.each(dataForTable, function (data) {
        imageDataObj.reportId = data.reportId;
        imageDataObj.prefecturalArea = data.prefecturalArea;
        imageDataObj.exchangeOfficeName = data.exchangeOfficeName;
        imageDataObj.assetInfo = data.assetInfo;
        imageDataObj.photographygTarget = data.photographygTarget;
        imageDataObj.fileName = data.fileName;
        imageDataObj.tag = data.tag;
        imageDataObj.tagTip = data.tagTip;
        imageDataObj.resultTag = data.resultTag;
        imageDataObj.user = data.user;
        imageDataObj.playerId = data.playerId;
        imageDataObj.fileUrl = data.fileUrl;
        const dateObj = new Date(data.occurred_datetime);
        imageDataObj.dateTime = dateObj
          .toISOString()
          .slice(0, 19)
          .replace("T", " ");
        imageDataObj.latitude = data.latitude;
        imageDataObj.longitude = data.longitude;
        imageArray.push(imageDataObj);
        imageDataObj = {};
      });
      setLoadedImages({});
      setTableData(imageArray);
    } else {
      setTableData([]);
      setTotalAssets(0);
      setLoadedImages({});
    }
  }, [reportDataNtt, filterType, isSaitamaReport]);

  useEffect(() => {
    const dataForTable = reportDataHokuden;
    if (dataForTable.length > 0) {
      let imageDataObj = {},
        imageArray = [];
      _.each(dataForTable, function (data) {
        imageDataObj.reportId = data.reportId;
        imageDataObj.powerPoleNumber = data.powerPoleNumber;
        imageDataObj.areaName = data.areaName;
        imageDataObj.branchCode = data.branchCode;
        imageDataObj.fileName = data.fileName;
        imageDataObj.tag = data.tag;
        imageDataObj.tagTip = data.tagTip;
        imageDataObj.resultTag = data.resultTag;
        imageDataObj.user = data.user;
        imageDataObj.playerId = data.playerId;
        imageDataObj.fileUrl = data.fileUrl;
        const dateObj = new Date(data.occurred_datetime);
        imageDataObj.dateTime = dateObj
          .toISOString()
          .slice(0, 19)
          .replace("T", " ");
        imageDataObj.latitude = data.latitude;
        imageDataObj.longitude = data.longitude;
        imageArray.push(imageDataObj);
        imageDataObj = {};
      });
      setLoadedImages({});
      setTableData(imageArray);
    } else {
      setTableData([]);
      setTotalAssets(0);
      setLoadedImages({});
    }
  }, [reportDataHokuden, filterType]);

  useEffect(() => {
    const dataForTable = reportDataMission;
    if (dataForTable.length > 0) {
      let imageDataObj = {},
        imageArray = [];
      _.each(dataForTable, function (data) {
        imageDataObj.reportId = data.reportId;
        imageDataObj.venueName = data.venueName;
        imageDataObj.fileName = data.fileName;
        imageDataObj.tag = data.tag;
        imageDataObj.tagTip = data.tagTip;
        imageDataObj.resultTag = data.resultTag;
        imageDataObj.user = data.user;
        imageDataObj.playerId = data.playerId;
        imageDataObj.fileUrl = data.fileUrl;
        const dateObj = new Date(data.occurred_datetime);
        imageDataObj.dateTime = dateObj
          .toISOString()
          .slice(0, 19)
          .replace("T", " ");
        imageDataObj.latitude = data.latitude;
        imageDataObj.longitude = data.longitude;
        imageArray.push(imageDataObj);
        imageDataObj = {};
      });
      setLoadedImages({});
      setTableData(imageArray);
    } else {
      setTableData([]);
      setTotalAssets(0);
      setLoadedImages({});
    }
  }, [reportDataMission, filterType]);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    setCurrentPageNumber(value);
  };

  const getViewUrlView = (reportId) => `/report-viewer/${reportId}`;

  const pageCount = Math.ceil(totalAssets / itemsPerPage);

  const handleMouseEnter = (imageUrl, e) => {
    setHoveredImage(imageUrl);
    setHoverPosition({ x: e.clientX, y: e.clientY });
  };

  const handleMouseMove = (e) => {
    setHoverPosition({ x: e.clientX, y: e.clientY });
  };

  const handleMouseLeave = () => {
    setHoveredImage(null);
  };

  const handleImageLoad = (fileUrl) => {
    if (!fileUrl) return;
    setLoadedImages((prev) =>
      prev[fileUrl] ? prev : { ...prev, [fileUrl]: true }
    );
  };

  const handleImageError = (fileUrl) => {
    if (!fileUrl) return;
    setLoadedImages((prev) => ({ ...prev, [fileUrl]: true }));
  };

  const getImageRef = (fileUrl) => (node) => {
    if (node && node.complete) {
      handleImageLoad(fileUrl);
    }
  };

  const calculatePopupTop = (y) => {
    const viewportHeight = window.innerHeight;
    const top = y - popupHeight / 2;
    if (top < 0) return 0;
    if (top + popupHeight > viewportHeight) return viewportHeight - popupHeight;
    return top;
  };

  const calculatePopupLeft = (x) => {
    const viewportWidth = window.innerWidth;
    const left = x + 20;
    if (left + popupWidth > viewportWidth) return viewportWidth - popupWidth;
    return left;
  };

  const handleOpenMap = (lat, lng) => {
    setSelectedLatLng({ lat, lng });
    setMapDrawerOpen(true);
  };

  const handleCloseMap = () => {
    setMapDrawerOpen(false);
  };

  useEffect(() => {
    if (pageTopLoader) {
      setLoadedImages({});
      setLoadCycle((prev) => prev + 1);
    }
  }, [pageTopLoader]);

  return (
    <>
      <Grid
        container
        direction="column"
        justifyContent={"center"}
        className={open ? classes.tableWrapperSlim : classes.tableWrapper}
      >
        <Grid item className={classes.filterItem}>
          <TableContainer component={Paper} className={classes.tableContainer}>
            <Table stickyHeader className={classes.table}>
              <TableHead className={classes.tableHead}>
                <TableRow className={classes.stickyHeader}>
                  {activeDownload && (
                    <TableCell align="center">
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Checkbox
                          checked={
                            downloadImage.length > 0 &&
                            downloadImage.length === tableData.length
                          } // 全選択判定
                          indeterminate={
                            downloadImage.length > 0 &&
                            downloadImage.length < tableData.length
                          } // 部分選択時
                          onChange={(e) => {
                            if (e.target.checked) {
                              // 全選択
                              setDownloadImage(
                                tableData.map((row) => row.fileUrl)
                              );
                            } else {
                              // リセット
                              setDownloadImage([]);
                            }
                          }}
                          inputProps={{ "aria-label": "select all" }}
                        />
                        <Tooltip
                          title={
                            <div>
                              一括ダウンロード選択
                              <br />
                              <small>※最大10件まで選択可能です</small>
                            </div>
                          }
                          placement="top"
                          classes={{ tooltip: classes.tooltip }}
                        >
                          <HelpIcon fontSize="small" className={classes.icon} />
                        </Tooltip>
                      </div>
                    </TableCell>
                  )}
                  <TableCell className={classes.thumb}></TableCell>
                  {isSaitamaReport && (
                    <TableCell align="center">管理ID</TableCell>
                  )}
                  {companyName === "tepco" && (
                    <>
                      <TableCell align="center">標識番号</TableCell>
                      <TableCell align="center">標識名</TableCell>
                      <TableCell align="center">設備番号</TableCell>
                      <TableCell align="center">スキーマ<br />番号</TableCell>
                    </>
                  )}
                  {companyName === "ntt" && (
                    <>
                      <TableCell align="center">県域</TableCell>
                      <TableCell align="center">収容局名</TableCell>
                      <TableCell align="center">設備番号</TableCell>
                      <TableCell align="center">撮影対象</TableCell>
                    </>
                  )}
                  {(companyName === "ntt-ft" || companyName === "okiden") && !isSaitamaReport && (
                    <>
                      <TableCell align="center">県域</TableCell>
                      <TableCell align="center">収容局名</TableCell>
                      <TableCell align="center">電柱識別番号</TableCell>
                      <TableCell align="center">撮影対象</TableCell>
                    </>
                  )}
                  {companyName === "hokuden" && (
                    <>
                      <TableCell align="center">電柱番号</TableCell>
                      <TableCell align="center">市町村名</TableCell>
                      <TableCell align="center">支店コード</TableCell>
                      <TableCell align="center"></TableCell>
                    </>
                  )}
                  {companyName === "mitsubishi" && (
                    <>
                      <TableCell align="center">識別情報</TableCell>
                      <TableCell align="center"></TableCell>
                    </>
                  )}
                  {companyName === "common" && (
                    <>
                      <TableCell align="center">自治体名</TableCell>
                      <TableCell align="center">都道府県</TableCell>
                      <TableCell align="center">アセットタイプ</TableCell>
                    </>
                  )}
                  <TableCell align="left">報告タグ</TableCell>
                  {companyName === "tepco" && (
                    <TableCell align="center">
                      報告確認結果
                      <IconButton
                          className={classes.iconTooltip}
                          color="inherit"
                          aria-label="open drawer"
                          onClick={open ? handleDrawerClose : handleDrawerOpen}
                          disableRipple={true}
                          disableFocusRipple={true}
                        >
                        <HelpIcon
                          fontSize="small"
                        />
                      </IconButton>
                    </TableCell>
                  )}
                  <TableCell align="center">報告者</TableCell>
                  <TableCell align="center">Player ID</TableCell>
                  <TableCell align="center">報告日時</TableCell>
                  <TableCell align="center">詳細</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tableData.length > 0 &&
                  tableData.map((row, index) => (
                    <TableRow className={classes.tableRow} key={index}>
                      {activeDownload && (
                        <TableCell style={{ width: "2%" }} align="center">
                          <Checkbox
                            checked={downloadImage.includes(row.fileUrl)}
                            onChange={(e) => handleImageSelect(e, row.fileUrl)}
                            inputProps={{
                              "download-image": "primary checkbox",
                            }}
                            disabled={downloadImage.length >= 10}
                          />
                        </TableCell>
                      )}
                      <TableCell
                        component="th"
                        scope="row"
                        style={{ width: "10%" }}
                      >
                        {row.fileUrl &&
                        <Link to={getViewUrlView(row.reportId)} target="_blank">
                          <div className={classes.thumbWrapper}>
                            {!(!pageTopLoader && loadedImages[row.fileUrl]) && (
                              <div className={classes.thumbSkeleton} />
                            )}
                            <img
                              key={`${row.fileUrl}-${loadCycle}`}
                              src={row.fileUrl}
                              height="90"
                              width="90"
                              loading="lazy"
                              ref={getImageRef(row.fileUrl)}
                              onLoad={() => handleImageLoad(row.fileUrl)}
                              onError={() => handleImageError(row.fileUrl)}
                              onMouseEnter={(e) =>
                                handleMouseEnter(row.fileUrl, e)
                              }
                              onMouseMove={handleMouseMove}
                              onMouseLeave={handleMouseLeave}
                              style={{
                                borderRadius: 6,
                                display: "block",
                                opacity: !pageTopLoader && loadedImages[row.fileUrl] ? 1 : 0,
                                transition: "opacity 0.35s ease-out",
                              }}
                            />
                          </div>
                        </Link>
                        }
                        {!row.fileUrl &&
                          <div className={classes.noImageBox}>
                            No Image
                          </div>
                        }
                      </TableCell>
                      {isSaitamaReport && (
                        <TableCell style={{ width: "15%" }} align="center">
                          {row.saitamaManagementId || "--"}
                        </TableCell>
                      )}
                      {companyName === "tepco" && (
                        <>
                          <TableCell style={{ width: "7%" }} align="center">
                            {row.signNumber}
                          </TableCell>
                          <TableCell style={{ width: "7%" }} align="center">
                            {row.signName}
                          </TableCell>
                          <TableCell style={{ width: "7%" }} align="center">
                            {row.name}
                          </TableCell>
                          <TableCell style={{ width: "7%" }} align="center">
                            {row.placeCode}
                          </TableCell>
                        </>
                      )}
                      {(companyName.includes("ntt") || companyName === "okiden") && !isSaitamaReport && (
                        <>
                          <TableCell style={{ width: "7%" }} align="center">
                            {row.prefecturalArea}
                          </TableCell>
                          <TableCell style={{ width: "7%" }} align="center">
                            {row.exchangeOfficeName}
                          </TableCell>
                          <TableCell style={{ width: "7%" }} align="center">
                            {row.assetInfo}
                          </TableCell>
                          <TableCell style={{ width: "7%" }} align="center">
                            {row.photographygTarget}
                          </TableCell>
                        </>
                      )}
                      {companyName === "hokuden" && (
                        <>
                          <TableCell style={{ width: "7%" }} align="center">
                            {row.powerPoleNumber}
                          </TableCell>
                          <TableCell style={{ width: "7%" }} align="center">
                            {row.areaName}
                          </TableCell>
                          <TableCell style={{ width: "7%" }} align="center">
                            {row.branchCode}
                          </TableCell>
                        </>
                      )}
                      {companyName === "mitsubishi" && (
                        <>
                          <TableCell style={{ width: "14%" }} align="center">
                            {row.venueName}
                          </TableCell>
                        </>
                      )}
                      {companyName === "common" && (
                        <>
                          <TableCell style={{ width: "9%" }} align="center">
                            {row.municipalityName}
                          </TableCell>
                          <TableCell style={{ width: "9%" }} align="center">
                            {row.prefectureName}
                          </TableCell>
                          <TableCell style={{ width: "12%" }} align="center">
                            {row.assetTypeName}
                          </TableCell>
                        </>
                      )}
                      <TableCell style={{ width: "30%" }} align="left">
                        {tagData !== null && tagData.length > 0 && (
                          <div className={classes.tagWrapper}>
                            {row.tag.length > 0 &&
                              row.tag.map((tag, index) => (
                                <Tooltip
                                  title={tag == "その他"
                                    ? row.tagTip
                                    : row.tagTip[index]
                                  }
                                  placement="top"
                                  key={index}
                                >
                                  <Chip
                                    style={{
                                      backgroundColor: "#ffed6f",
                                      color: "black",
                                    }}
                                    size="small"
                                    label={tag}
                                  ></Chip>
                                </Tooltip>
                              ))}
                          </div>
                        )}
                      </TableCell>
                      {companyName === "tepco" && (
                      <TableCell style={{ width: "20%" }} align="left">
                        <div className={classes.tagWrapper}>
                          {row.resultTag.length > 0 &&
                            row.resultTag.map((tag, index) => (
                              <Chip
                                key={index}
                                style={{
                                  backgroundColor: "#ffed6f",
                                  color: "black",
                                }}
                                size="small"
                                label={tag}
                              ></Chip>
                            ))}
                        </div>
                      </TableCell>
                      )}
                      <TableCell style={{ width: "20%" }} align="center">
                        {row.user}
                      </TableCell>
                      <TableCell style={{ width: "20%" }} align="center">
                        {row.playerId}
                      </TableCell>
                      <TableCell style={{ width: "20%" }} align="center">
                        {row.dateTime}
                      </TableCell>
                      <TableCell>
                        <Link
                          to={getViewUrlView(row.reportId)}
                          target="_blank"
                        >
                          <IconButton>
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </Link>
                        <IconButton onClick={() => handleOpenMap(row.latitude, row.longitude)}>
                          <MapIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                {tableData.length === 0 && (
                  <TableRow className={classes.tableRowNone}>
                    <TableCell colSpan={9} className={classes.noDataCell}>
                      データがありません
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
        {tableData.length > 0 && (
          <Grid item>
            <div className={classes.pagination}>
              <Pagination
                count={pageCount}
                page={currentPage}
                onChange={handlePageChange}
                color="secondary"
              />
            </div>
          </Grid>
        )}
      </Grid>
      {hoveredImage && (
        <div
          className={classes.imagePopup}
          style={{
            top: calculatePopupTop(hoverPosition.y),
            left: calculatePopupLeft(hoverPosition.x),
          }}
        >
          <img src={hoveredImage} alt="Preview" width={300} />
        </div>
      )}
      <Drawer anchor="right" open={mapDrawerOpen} onClose={handleCloseMap}>
        <Box
          style={{
            width: 480,
            height: '100vh', // 高さを画面にフィットさせる
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box p={1}>
            <Typography variant="h6">電柱位置</Typography>
          </Box>
          <Box
            style={{
              flex: 1,
              overflowY: 'auto',
            }}
          >
            <iframe
              title="map"
              width="100%"
              height="100%"
              frameBorder="0"
              style={{ border: 0 }}
              loading="lazy"
              allowFullScreen
              src={`https://www.google.com/maps?q=${selectedLatLng.lat},${selectedLatLng.lng}&z=16&hl=ja&output=embed`}
            />
          </Box>
          <Box p={2} style={{ textAlign: 'center' }}>
            <Button
              href={`https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${selectedLatLng.lat},${selectedLatLng.lng}`}
              target="_blank"
              rel="noopener noreferrer"
              variant="outlined"
            >
              ストリートビューで開く
            </Button>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

ReportTable.prototypes = {
  classes: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  periodType: state.app.periodType,
  filterType: state.app.filterType,
  reportData: state.report.reportData,
  filteredTotal: state.report.filteredTotal,
  reportDataNtt: state.report.reportDataNtt,
  reportDataHokuden: state.report.reportDataHokuden,
  reportDataMission: state.report.reportDataMission,
  pageTopLoader: state.app.pageTopLoader,
  selectedSeason: state.app.selectedSeason,
});

const mapDispatchToProps = (dispatch) => ({
  setCurrentPageNumber: (param) => dispatch(setCurrentPageNumber(param)),
  setSelectedTag: (param) => dispatch(setSelectedTag(param)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(null, { withTheme: true })(ReportTable));
